import { useEffect, useState } from 'react';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import { getWaterAlertSettingsURL, updateWaterAlertSettingsURL } from '../../../utils/urls';

const MAX_ADDITIONAL_CONTACTS = 5;

const ALERT_OPTIONS = [
  { key: 'LOW_LEVEL', label: 'Water level low' },
  { key: 'HIGH_LEVEL', label: 'Tank almost overflowing' },
  { key: 'OFFLINE', label: 'Sensor stopped reporting' },
  { key: 'LOW_BATTERY', label: 'Sensor battery low' },
];

const EMPTY_SETTINGS = {
  enabled: true,
  primaryContact: { name: '', phone: '' },
  additionalContacts: [],
  notifyOn: { LOW_LEVEL: true, HIGH_LEVEL: true, OFFLINE: false, LOW_BATTERY: false },
  emailEnabled: true,
  managerEmail: '',
  caretakerEmail: '',
  additionalEmailRecipients: [],
};

// Loose check only; the server normalises and validates the number properly
const looksLikePhone = (value) => /^\+?[\d\s\-()]{9,16}$/.test(value.trim());

const WaterAlertsTab = ({ facilityId }) => {
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!facilityId) return;
    const load = async () => {
      setLoading(true);
      const response = await makeRequest2(getWaterAlertSettingsURL(facilityId), 'GET');
      if (response.success) {
        setSettings({ ...EMPTY_SETTINGS, ...response.data.data });
      } else {
        toastify(response.error || 'Failed to load water alert settings', 'error');
      }
      setLoading(false);
    };
    load();
  }, [facilityId]);

  const setPrimary = (field, value) =>
    setSettings((prev) => ({ ...prev, primaryContact: { ...prev.primaryContact, [field]: value } }));

  const setAdditional = (index, field, value) =>
    setSettings((prev) => ({
      ...prev,
      additionalContacts: prev.additionalContacts.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
    }));

  const addContact = () =>
    setSettings((prev) => ({ ...prev, additionalContacts: [...prev.additionalContacts, { name: '', phone: '' }] }));

  const removeContact = (index) =>
    setSettings((prev) => ({ ...prev, additionalContacts: prev.additionalContacts.filter((_, i) => i !== index) }));

  const setAdditionalEmail = (index, field, value) =>
    setSettings((prev) => ({
      ...prev,
      additionalEmailRecipients: prev.additionalEmailRecipients.map((c, i) =>
        i === index ? { ...c, [field]: value } : c
      ),
    }));

  const addEmailRecipient = () =>
    setSettings((prev) => ({
      ...prev,
      additionalEmailRecipients: [...prev.additionalEmailRecipients, { name: '', email: '' }],
    }));

  const removeEmailRecipient = (index) =>
    setSettings((prev) => ({
      ...prev,
      additionalEmailRecipients: prev.additionalEmailRecipients.filter((_, i) => i !== index),
    }));

  const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

  const toggleAlert = (key) =>
    setSettings((prev) => ({ ...prev, notifyOn: { ...prev.notifyOn, [key]: !prev.notifyOn[key] } }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Rows left completely empty are ignored
    const additionalContacts = settings.additionalContacts
      .map((c) => ({ name: c.name.trim(), phone: c.phone.trim() }))
      .filter((c) => c.name || c.phone);

    if (settings.primaryContact.phone.trim() && !looksLikePhone(settings.primaryContact.phone)) {
      toastify('Enter a valid phone number for the primary contact', 'error');
      return;
    }
    if (additionalContacts.some((c) => !looksLikePhone(c.phone))) {
      toastify('Every additional contact needs a valid phone number', 'error');
      return;
    }

    const additionalEmailRecipients = settings.additionalEmailRecipients
      .map((c) => ({ name: c.name.trim(), email: c.email.trim().toLowerCase() }))
      .filter((c) => c.name || c.email);

    if (settings.managerEmail.trim() && !looksLikeEmail(settings.managerEmail)) {
      toastify('Enter a valid facility manager email address', 'error');
      return;
    }
    if (settings.caretakerEmail.trim() && !looksLikeEmail(settings.caretakerEmail)) {
      toastify('Enter a valid caretaker email address', 'error');
      return;
    }
    if (additionalEmailRecipients.some((c) => !looksLikeEmail(c.email))) {
      toastify('Every additional recipient needs a valid email address', 'error');
      return;
    }
    const emailAddresses = [
      settings.managerEmail.trim().toLowerCase(),
      settings.caretakerEmail.trim().toLowerCase(),
      ...additionalEmailRecipients.map((c) => c.email),
    ].filter(Boolean);
    if (new Set(emailAddresses).size !== emailAddresses.length) {
      toastify('Each email address can only be listed once', 'error');
      return;
    }

    setSaving(true);
    const response = await makeRequest2(updateWaterAlertSettingsURL(facilityId), 'PUT', {
      enabled: settings.enabled,
      primaryContact: {
        name: settings.primaryContact.name.trim(),
        phone: settings.primaryContact.phone.trim(),
      },
      additionalContacts,
      notifyOn: settings.notifyOn,
      emailEnabled: settings.emailEnabled,
      managerEmail: settings.managerEmail.trim(),
      caretakerEmail: settings.caretakerEmail.trim(),
      additionalEmailRecipients,
    });
    setSaving(false);

    if (response.success) {
      setSettings({ ...EMPTY_SETTINGS, ...response.data.data });
      toastify('Water alert settings saved', 'success');
    } else {
      toastify(response.error || 'Failed to save water alert settings', 'error');
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-muted">
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Loading...
      </div>
    );
  }

  return (
    <div className="row p-3">
      <div className="col-lg-8">
        <div className="card">
          <div className="card-body">
            <h5>Water Level SMS Alerts</h5>
            <p className="text-muted mb-0">
              Text the people below when a water tank needs attention. Messages are sent with this facility&apos;s SMS
              settings.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="form-check form-switch mt-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="water-alerts-enabled"
                  checked={settings.enabled}
                  onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="water-alerts-enabled">
                  Send SMS alerts
                </label>
              </div>

              <h6 className="mt-4">Facility manager / caretaker</h6>
              <div className="row g-2">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Name"
                    maxLength={100}
                    value={settings.primaryContact.name}
                    onChange={(e) => setPrimary('name', e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="Phone, e.g. 0712 345 678"
                    value={settings.primaryContact.phone}
                    onChange={(e) => setPrimary('phone', e.target.value)}
                  />
                </div>
              </div>

              <h6 className="mt-4">Additional contacts</h6>
              {settings.additionalContacts.length === 0 && (
                <p className="text-muted small">No additional contacts. They receive the same alerts as the contact above.</p>
              )}
              {settings.additionalContacts.map((contact, index) => (
                <div className="row g-2 mb-2" key={index}>
                  <div className="col-md-5">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Name"
                      maxLength={100}
                      value={contact.name}
                      onChange={(e) => setAdditional(index, 'name', e.target.value)}
                    />
                  </div>
                  <div className="col-md-5">
                    <input
                      type="tel"
                      className="form-control"
                      placeholder="Phone"
                      value={contact.phone}
                      onChange={(e) => setAdditional(index, 'phone', e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <button
                      type="button"
                      className="btn btn-outline-danger w-100"
                      onClick={() => removeContact(index)}
                      aria-label="Remove contact"
                    >
                      <i className="ti ti-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline-primary btn-sm mt-1"
                onClick={addContact}
                disabled={settings.additionalContacts.length >= MAX_ADDITIONAL_CONTACTS}
              >
                <i className="ti ti-plus me-1"></i>Add contact
              </button>

              <h6 className="mt-4">Send an SMS when</h6>
              {ALERT_OPTIONS.map(({ key, label }) => (
                <div className="form-check" key={key}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`water-alert-${key}`}
                    checked={!!settings.notifyOn[key]}
                    onChange={() => toggleAlert(key)}
                  />
                  <label className="form-check-label" htmlFor={`water-alert-${key}`}>
                    {label}
                  </label>
                </div>
              ))}
              <p className="text-muted small mt-2 mb-0">
                The low and high percentages are set per tank on the Water Level Sensors page.
              </p>

              <hr className="my-4" />
              <h5>Email Alerts</h5>
              <p className="text-muted mb-0">
                Send water-level alerts to the facility manager, caretaker and any additional recipients below.
              </p>

              <div className="form-check form-switch mt-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="water-email-alerts-enabled"
                  checked={settings.emailEnabled}
                  onChange={(e) => setSettings({ ...settings, emailEnabled: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="water-email-alerts-enabled">
                  Send email alerts
                </label>
              </div>

              <h6 className="mt-4">Facility manager</h6>
              <div className="row g-2">
                <div className="col-md-12">
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Facility manager email"
                    maxLength={254}
                    value={settings.managerEmail}
                    onChange={(e) => setSettings({ ...settings, managerEmail: e.target.value })}
                    disabled={!settings.emailEnabled}
                  />
                </div>
              </div>

              <h6 className="mt-3">Caretaker</h6>
              <div className="row g-2">
                <div className="col-md-12">
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Caretaker email"
                    maxLength={254}
                    value={settings.caretakerEmail}
                    onChange={(e) => setSettings({ ...settings, caretakerEmail: e.target.value })}
                    disabled={!settings.emailEnabled}
                  />
                </div>
              </div>

              <h6 className="mt-4">Additional email recipients</h6>
              {settings.additionalEmailRecipients.length === 0 && (
                <p className="text-muted small">No additional email recipients added.</p>
              )}
              {settings.additionalEmailRecipients.map((contact, index) => (
                <div className="row g-2 mb-2" key={index}>
                  <div className="col-md-5">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Name"
                      maxLength={100}
                      value={contact.name}
                      onChange={(e) => setAdditionalEmail(index, 'name', e.target.value)}
                      disabled={!settings.emailEnabled}
                    />
                  </div>
                  <div className="col-md-5">
                    <input
                      type="email"
                      className="form-control"
                      placeholder="Email address"
                      maxLength={254}
                      value={contact.email}
                      onChange={(e) => setAdditionalEmail(index, 'email', e.target.value)}
                      disabled={!settings.emailEnabled}
                    />
                  </div>
                  <div className="col-md-2">
                    <button
                      type="button"
                      className="btn btn-outline-danger w-100"
                      onClick={() => removeEmailRecipient(index)}
                      aria-label="Remove email recipient"
                      disabled={!settings.emailEnabled}
                    >
                      <i className="ti ti-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline-primary btn-sm mt-1"
                onClick={addEmailRecipient}
                disabled={!settings.emailEnabled || settings.additionalEmailRecipients.length >= 20}
              >
                <i className="ti ti-plus me-1"></i>Add email recipient
              </button>

              <div className="mt-4">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaterAlertsTab;